<script setup lang="ts">
/**
 * „Meinten Sie …“: one line that offers a corrected spelling of what the user
 * typed — „Meinten Sie Dichtung · Ignorieren · „Dichtnug“ ist richtig“. The
 * suggestion is a link that takes it over; „Ignorieren“ keeps the typed text
 * this time (nothing is stored), „„Dichtnug“ ist richtig“ keeps it for good (no more suggestion).
 * One row for the chip query input and the command palette.
 *
 * Opt-in (`neverSuggest`): a fourth link, „„Dichtung“ nicht mehr vorschlagen“,
 * that asks the parent to stop offering the suggested word. It is not there
 * unless the parent opts in. `confirmable` (default on) set to `false` takes
 * the confirm link away, for a suggestion that is no spelling of the typed word
 * (a synonym): there is nothing to „confirm“.
 *
 * Presentational: it emits `accept`, `keep`, `confirm` and `never-suggest`
 * without a payload —
 * the parent binds them to the term and the query generation the suggestion
 * was made for, so a late answer cannot hit a newer query. The row is no live
 * region and has no role: the parent announces the suggestion.
 *
 * The labels (`prefixLabel`, `keepLabel`, `confirmLabel`, `neverSuggestLabel`;
 * `{original}` in the confirm one is replaced by what was typed, `{text}` in
 * the never-suggest one by the suggested word) default to German;
 * `NX_SUGGESTION_ROW_LABELS_EN` is the English set.
 */
import { computed } from 'vue';
import { NxButton } from '../Button';

const props = withDefaults(
  defineProps<{
    /** The suggested spelling — the link that takes it over. */
    text: string;
    /** What the user typed, named in the confirm link. */
    original: string;
    /** Before the suggestion. */
    prefixLabel?: string;
    /** The link that keeps the typed text, this time. */
    keepLabel?: string;
    /** The link that keeps the typed text for good; `{original}` is replaced by it. */
    confirmLabel?: string;
    /** Default on. `false` hides the confirm link — for a suggestion that is no spelling of the typed word. */
    confirmable?: boolean;
    /** Opt-in: shows the link that stops suggesting the suggested word and emits `never-suggest`. */
    neverSuggest?: boolean;
    /** The never-suggest link; `{text}` is replaced by the suggested word. */
    neverSuggestLabel?: string;
  }>(),
  {
    prefixLabel: 'Meinten Sie',
    keepLabel: 'Ignorieren',
    confirmLabel: '„{original}“ ist richtig',
    confirmable: true,
    neverSuggest: false,
    neverSuggestLabel: '„{text}“ nicht mehr vorschlagen',
  }
);

const emit = defineEmits<{
  /** Take the suggestion over. */
  accept: [];
  /** Keep what was typed, this time. */
  keep: [];
  /** Keep what was typed for good. */
  confirm: [];
  /** Stop suggesting the suggested word (only with `neverSuggest`). */
  'never-suggest': [];
}>();

/** Between the links; decorative, hidden from screen readers. */
const SEPARATOR = '·';

// A replacer function: what was typed goes in literally, `$&` and `$1` included.
const confirmText = computed(() =>
  props.confirmLabel.replaceAll('{original}', () => props.original)
);

const neverSuggestText = computed(() =>
  props.neverSuggestLabel.replaceAll('{text}', () => props.text)
);
</script>

<template>
  <div class="nx-suggestion-row" data-testid="nx-suggestion-row">
    <span class="nx-suggestion-row__prefix">{{ props.prefixLabel }}</span>
    <NxButton variant="link" data-testid="nx-suggestion-accept" @click="emit('accept')">
      {{ props.text }}
    </NxButton>
    <span class="nx-suggestion-row__separator" aria-hidden="true">{{ SEPARATOR }}</span>
    <NxButton variant="link" data-testid="nx-suggestion-keep" @click="emit('keep')">
      {{ props.keepLabel }}
    </NxButton>
    <template v-if="props.confirmable">
      <span class="nx-suggestion-row__separator" aria-hidden="true">{{ SEPARATOR }}</span>
      <NxButton variant="link" data-testid="nx-suggestion-confirm" @click="emit('confirm')">
        {{ confirmText }}
      </NxButton>
    </template>
    <template v-if="props.neverSuggest">
      <span class="nx-suggestion-row__separator" aria-hidden="true">{{ SEPARATOR }}</span>
      <NxButton
        variant="link"
        data-testid="nx-suggestion-never-suggest"
        @click="emit('never-suggest')"
      >
        {{ neverSuggestText }}
      </NxButton>
    </template>
  </div>
</template>

<style scoped>
.nx-suggestion-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--nxt-space-2xs) var(--nxt-space-xs);
  min-width: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}
</style>
